import {
  FumDocData, BoletaDocData, ActaDocData,
  ConstanciaDocData, RodDocData, HistorialDocData, NominaDocData,
  CertificadoDocData, BoletaSIAGIEDocData, BoletaSIAGIEArea,
} from 'types/documento.types';

// ============================================================
// documentoPdf.ts — HTML renderers para generación de PDFs SIAGIE
// Cada función produce un string HTML para pasar a exportarPDF()
// ============================================================

function fmt(s: string | number | null | undefined): string {
  return s != null && s !== '' ? String(s) : '—';
}

function fmtFase(fase: string | null | undefined): string {
  if (!fase) return '—';
  return fase === 'RECUPERACION' ? 'Recuperación' : 'Regular';
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function htmlFum(d: FumDocData): string {
  const { estudiante, matricula, tutores, salud } = d;

  const filasInst = `
    <tr>
      <td>${fmt(d.institucion.nombre)}</td>
      <td>${fmt(d.institucion.codigoModular)}</td>
      <td>${fmt(d.institucion.ugel)}</td>
      <td>${fmt(d.institucion.dre)}</td>
      <td>${fmt(d.institucion.directorNombre)}</td>
    </tr>`;

  const filasTutores = tutores.length === 0
    ? '<tr><td colspan="7" style="text-align:center;color:#94a3b8;padding:10px">Sin tutores registrados</td></tr>'
    : tutores.map(t => `
      <tr>
        <td>${fmt(t.apellidos)}, ${fmt(t.nombres)}</td>
        <td>${fmt(t.parentesco)}</td>
        <td>${fmt(t.dni)}</td>
        <td>${fmt(t.celular)}</td>
        <td>${fmt(t.email)}</td>
        <td>${fmt(t.ocupacion)}</td>
        <td style="text-align:center">${t.esContactoPrincipal ? 'Sí' : 'No'}</td>
      </tr>`).join('');

  const saludHtml = salud ? `
    <div class="section-hdr">Datos de Salud</div>
    <table>
      <thead><tr>
        <th>Peso (kg)</th><th>Talla (cm)</th><th>Tipo Sangre</th>
        <th>Alergias</th><th>Condición Especial</th><th>Seguro</th><th>N° Seguro</th>
      </tr></thead>
      <tbody><tr>
        <td style="text-align:center">${fmt(salud.pesoKg)}</td>
        <td style="text-align:center">${fmt(salud.tallaCm)}</td>
        <td style="text-align:center">${fmt(salud.tipoSangre)}</td>
        <td>${fmt(salud.alergias)}</td>
        <td>${fmt(salud.condicionEspecial)}</td>
        <td>${fmt(salud.seguroSalud)}</td>
        <td>${fmt(salud.numeroSeguro)}</td>
      </tr></tbody>
    </table>` : '';

  return `
    <div class="section-hdr">Institución Educativa</div>
    <table>
      <thead><tr><th>Nombre</th><th>Cód. Modular</th><th>UGEL</th><th>DRE</th><th>Director</th></tr></thead>
      <tbody>${filasInst}</tbody>
    </table>

    <div class="section-hdr">Datos del Estudiante</div>
    <table>
      <thead><tr>
        <th>Apellidos y Nombres</th><th>DNI</th>
        <th>Fecha de Nacimiento</th><th>Código SIAGIE</th>
        <th>Dirección</th><th>Teléfono</th>
      </tr></thead>
      <tbody><tr>
        <td>${fmt(estudiante.apellidos)}, ${fmt(estudiante.nombres)}</td>
        <td>${fmt(estudiante.dni)}</td>
        <td>${fmtDate(estudiante.fechaNacimiento)}</td>
        <td>${fmt(estudiante.codigoSiagie)}</td>
        <td>${fmt(estudiante.direccion)}</td>
        <td>${fmt(estudiante.telefono)}</td>
      </tr></tbody>
    </table>

    <div class="section-hdr">Datos de Matrícula</div>
    <table>
      <thead><tr>
        <th>Año</th><th>Grado</th><th>Nivel</th><th>Sección</th>
        <th>Código Matrícula</th><th>Fecha Matrícula</th>
      </tr></thead>
      <tbody><tr>
        <td style="text-align:center">${matricula.anio}</td>
        <td>${fmt(matricula.gradoNombre)}</td>
        <td>${fmt(matricula.gradoNivel)}</td>
        <td>${fmt(matricula.seccionNombre)}</td>
        <td>${fmt(matricula.codigoMatricula)}</td>
        <td>${fmtDate(matricula.fechaMatricula)}</td>
      </tr></tbody>
    </table>

    <div class="section-hdr">Tutores / Apoderados</div>
    <table>
      <thead><tr>
        <th>Nombre</th><th>Parentesco</th><th>DNI</th>
        <th>Celular</th><th>Email</th><th>Ocupación</th><th>Cont. Principal</th>
      </tr></thead>
      <tbody>${filasTutores}</tbody>
    </table>

    ${saludHtml}
  `;
}

// Bloque visual de asistencia para la boleta (4 contadores + porcentaje)
function renderAsistenciaBoleta(a: NonNullable<BoletaDocData['asistencia']>): string {
  const total = a.diasHabiles || 0;
  const pct   = total > 0 ? Math.round((a.asistencias / total) * 100) : 0;
  const pctColor = pct >= 90 ? '#15803d' : pct >= 75 ? '#b45309' : '#dc2626';

  const cell = (label: string, value: number, color: string, bg: string) => `
    <td style="border:1px solid #e2e8f0;padding:8px 10px;text-align:center;background:${bg}">
      <div style="font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:.04em;font-weight:600">
        ${label}
      </div>
      <div style="font-size:18px;font-weight:700;color:${color};font-family:monospace;margin-top:2px">
        ${value}
      </div>
    </td>`;

  return `
    <div style="margin-top:14px">
      <div style="font-size:10px;font-weight:700;color:#1e293b;text-transform:uppercase;
                   letter-spacing:.06em;margin-bottom:6px">
        Asistencia del período
      </div>
      <table style="width:100%;border-collapse:collapse">
        <tbody><tr>
          ${cell('Días hábiles',  a.diasHabiles,   '#1e293b', '#f8fafc')}
          ${cell('Asistencias',   a.asistencias,   '#15803d', '#f0fdf4')}
          ${cell('Inasistencias', a.inasistencias, '#dc2626', '#fef2f2')}
          ${cell('Tardanzas',     a.tardanzas,     '#b45309', '#fffbeb')}
          <td style="border:1px solid #e2e8f0;padding:8px 10px;text-align:center;background:#fff">
            <div style="font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:.04em;font-weight:600">
              % Asistencia
            </div>
            <div style="font-size:18px;font-weight:700;color:${pctColor};font-family:monospace;margin-top:2px">
              ${pct}%
            </div>
          </td>
        </tr></tbody>
      </table>
    </div>`;
}

export function htmlBoleta(d: BoletaDocData): string {
  const { institucion, estudiante, periodo, areas, promedioGeneral, asistencia } = d;

  // ── helpers locales ───────────────────────────────────────────
  const notaFmt = (n: number | null) =>
    n === null
      ? '<span style="color:#94a3b8;font-weight:700">—</span>'
      : `<span style="font-weight:700;color:${n >= 11 ? '#15803d' : '#dc2626'}">${n.toFixed(2)}</span>`;

  const califBadge = (c: string) => {
    const estilos: Record<string, string> = {
      AD: 'background:#d1fae5;color:#065f46',
      A:  'background:#dcfce7;color:#15803d',
      B:  'background:#fef3c7;color:#b45309',
      C:  'background:#fee2e2;color:#dc2626',
      EX: 'background:#e0e7ff;color:#3730a3',
    };
    const s = estilos[c?.toUpperCase()] ?? 'background:#f1f5f9;color:#475569';
    return `<span style="display:inline-block;padding:1px 8px;border-radius:99px;font-weight:700;font-size:10px;${s}">${fmt(c)}</span>`;
  };

  const exoneradoBadge = () =>
    `<span style="display:inline-block;padding:1px 8px;border-radius:99px;font-weight:700;font-size:10px;background:#e0e7ff;color:#3730a3">EX</span>`;

  const tipoLabel = periodo.tipo === 'RECUPERACION' ? ' &nbsp;—&nbsp; Recuperación' : '';

  // ── filas: área + subfilas de competencias ────────────────────
  const filas = areas.map(a => {
    const areaRow = `
      <tr style="background:#f1f5f9">
        <td style="font-weight:700;padding:5px 8px">${fmt(a.cursoNombre)}</td>
        <td style="text-align:center;padding:5px 8px">
          ${a.exonerado ? exoneradoBadge() : a.notaLetra !== null ? califBadge(a.notaLetra) : notaFmt(a.notaPromedio)}
        </td>
        <td style="padding:5px 8px;color:#64748b;font-size:9px">${fmt(a.observacion)}</td>
      </tr>`;

    const compRows = a.competencias.length === 0 ? '' : a.competencias.map(c => `
      <tr>
        <td style="padding:3px 8px 3px 24px;font-size:9px;color:#475569">
          <span style="color:#94a3b8;margin-right:4px">›</span>${fmt(c.competenciaNombre)}
        </td>
        <td style="text-align:center;padding:3px 8px">${califBadge(c.calificacion)}</td>
        <td style="padding:3px 8px;font-size:9px;color:#64748b;font-style:italic">
          ${fmt(c.conclusionDescriptiva)}
        </td>
      </tr>`).join('');

    return areaRow + compRows;
  }).join('');

  // ── promedio general ──────────────────────────────────────────
  const promHtml = promedioGeneral !== null
    ? `<div style="margin-top:10px;text-align:right;font-size:11px;color:#1e293b">
         Promedio General del Período:
         <span style="font-size:16px;font-weight:700;margin-left:8px;
                       color:${promedioGeneral >= 11 ? '#15803d' : '#dc2626'}">
           ${promedioGeneral.toFixed(2)}
         </span>
       </div>`
    : '';

  // ── código SIAGIE del estudiante (siempre se muestra; "—" si null) ──
  const siagieCell = estudiante.codigoSiagie
    ? `<td style="text-align:center;font-family:monospace">${fmt(estudiante.codigoSiagie)}</td>`
    : `<td style="text-align:center;color:#94a3b8;font-style:italic">No registrado</td>`;

  return `
    <!-- ── Cabecera institucional SIAGIE ── -->
    <div style="border:1px solid #cbd5e1;border-radius:6px;padding:10px 14px;
                margin-bottom:14px;background:#f8fafc">
      <div style="font-size:9px;color:#64748b;margin-bottom:4px">
        DRE: <strong>${fmt(institucion.dre)}</strong>
        &nbsp;|&nbsp;
        UGEL: <strong>${fmt(institucion.ugel)}</strong>
        &nbsp;|&nbsp;
        Turno: <strong>${fmt(institucion.turno)}</strong>
      </div>
      <div style="font-size:13px;font-weight:700;color:#1e3a8a">${fmt(institucion.nombre)}</div>
      <div style="font-size:9px;color:#64748b;margin-top:2px">
        Código Modular: <strong>${fmt(institucion.codigoModular)}</strong>
        &nbsp;|&nbsp;
        ${fmt(institucion.direccion)}
      </div>
      <div style="margin-top:9px;text-align:center;border-top:1px solid #e2e8f0;padding-top:8px">
        <div style="font-size:12px;font-weight:700;color:#1e293b;text-transform:uppercase;
                    letter-spacing:.04em">
          Boleta de Información al Estudiante y Padre de Familia
        </div>
        <div style="font-size:10px;color:#475569;margin-top:2px">
          Año Lectivo <strong>${estudiante.anio}</strong>
          &nbsp;—&nbsp;
          <strong>${fmt(periodo.nombre)}</strong>${tipoLabel}
          &nbsp;&nbsp;
          (${fmtDate(periodo.fechaInicio)} al ${fmtDate(periodo.fechaFin)})
        </div>
      </div>
    </div>

    <!-- ── Datos del estudiante ── -->
    <table style="margin-bottom:12px">
      <thead><tr>
        <th>Apellidos y Nombres</th>
        <th style="text-align:center">DNI</th>
        <th style="text-align:center">Cód. SIAGIE</th>
        <th style="text-align:center">Grado</th>
        <th style="text-align:center">Sección</th>
        <th style="text-align:center">Nivel</th>
      </tr></thead>
      <tbody><tr>
        <td style="font-weight:700">${fmt(estudiante.apellidos)}, ${fmt(estudiante.nombres)}</td>
        <td style="text-align:center;font-family:monospace">${fmt(estudiante.dni)}</td>
        ${siagieCell}
        <td style="text-align:center">${fmt(estudiante.gradoNombre)}</td>
        <td style="text-align:center">${fmt(estudiante.seccionNombre)}</td>
        <td style="text-align:center">${fmt(estudiante.gradoNivel)}</td>
      </tr></tbody>
    </table>

    <!-- ── Áreas curriculares y competencias ── -->
    <table>
      <thead><tr>
        <th>Área Curricular / Competencia</th>
        <th style="text-align:center;min-width:75px">Nota / Calif.</th>
        <th>Observación / Conclusión Descriptiva</th>
      </tr></thead>
      <tbody>
        ${filas || '<tr><td colspan="3" style="text-align:center;color:#94a3b8;padding:10px">Sin áreas registradas</td></tr>'}
      </tbody>
    </table>

    ${promHtml}

    <!-- ── Asistencia del período ── -->
    ${asistencia ? renderAsistenciaBoleta(asistencia) : ''}

    <!-- ── Firmas ── -->
    <table style="margin-top:44px;border:none">
      <tbody><tr>
        <td style="text-align:center;padding:0 10px;border:none">
          <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:9px;color:#64748b;margin-top:28px">
            V°B° Padre/Madre de Familia o Apoderado(a)
          </div>
        </td>
        <td style="text-align:center;padding:0 10px;border:none">
          <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:9px;color:#64748b;margin-top:28px">
            Docente Tutor(a)
          </div>
        </td>
        <td style="text-align:center;padding:0 10px;border:none">
          <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:9px;color:#64748b;margin-top:28px">
            Director(a)
            <div style="font-weight:700;color:#1e293b;margin-top:2px">${fmt(institucion.directorNombre)}</div>
          </div>
        </td>
      </tr></tbody>
    </table>

    <!-- ── Escala de calificación (pie de página) ── -->
    <div style="margin-top:16px;padding:6px 10px;background:#f8fafc;
                border:1px solid #e2e8f0;border-radius:4px;font-size:8px;color:#64748b">
      <strong>Escala de calificación:</strong>
      &nbsp;
      <span style="background:#d1fae5;color:#065f46;padding:1px 5px;border-radius:99px;font-weight:700;margin-right:4px">AD</span> Logro destacado (18–20)
      &nbsp;
      <span style="background:#dcfce7;color:#15803d;padding:1px 5px;border-radius:99px;font-weight:700;margin-right:4px">A</span> Logro esperado (14–17)
      &nbsp;
      <span style="background:#fef3c7;color:#b45309;padding:1px 5px;border-radius:99px;font-weight:700;margin-right:4px">B</span> En proceso (11–13)
      &nbsp;
      <span style="background:#fee2e2;color:#dc2626;padding:1px 5px;border-radius:99px;font-weight:700;margin-right:4px">C</span> En inicio (0–10)
    </div>
  `;
}

export function htmlActa(d: ActaDocData): string {
  const { seccion, cursos, estudiantes, resumen } = d;

  const thCursos = cursos.map(c =>
    `<th style="text-align:center;min-width:70px;max-width:90px;font-size:8px">${c.cursoNombre}</th>`
  ).join('');

  const filas = estudiantes.map(e => {
    const tdCursos = cursos.map(c => {
      const n = e.notasPorAsignacion[String(c.asignacionId)];
      const val = n != null ? n.toFixed(2) : '—';
      const color = n != null ? (n >= 11 ? '#15803d' : '#dc2626') : '#94a3b8';
      return `<td style="text-align:center;color:${color};font-weight:700">${val}</td>`;
    }).join('');

    return `
      <tr>
        <td style="text-align:center">${e.orden}</td>
        <td>${fmt(e.apellidos)}, ${fmt(e.nombres)}</td>
        <td>${fmt(e.dni)}</td>
        ${tdCursos}
        <td style="text-align:center;font-weight:700;color:${e.promedioAnual !== null && e.promedioAnual >= 11 ? '#15803d' : '#dc2626'}">
          ${e.promedioAnual !== null ? e.promedioAnual.toFixed(2) : '—'}
        </td>
        <td style="text-align:center;color:${e.areasDesaprobadas > 0 ? '#dc2626' : '#15803d'};font-weight:700">
          ${e.areasDesaprobadas}
        </td>
        <td><span class="badge">${fmt(e.estado)}</span></td>
        <td style="font-size:9px;color:#64748b">${fmt(e.observacion)}</td>
      </tr>`;
  }).join('');

  return `
    <div class="kpi-row">
      <div class="kpi"><div class="kpi-lbl">Grado</div><div class="kpi-val" style="font-size:13px">${fmt(seccion.gradoNombre)}</div></div>
      <div class="kpi"><div class="kpi-lbl">Sección</div><div class="kpi-val" style="font-size:13px">${fmt(seccion.seccionNombre)}</div></div>
      <div class="kpi"><div class="kpi-lbl">Nivel</div><div class="kpi-val" style="font-size:13px">${fmt(seccion.gradoNivel)}</div></div>
      <div class="kpi"><div class="kpi-lbl">Año Escolar</div><div class="kpi-val" style="font-size:13px">${seccion.anio}</div></div>
      <div class="kpi"><div class="kpi-lbl">Total Matriculados</div><div class="kpi-val">${seccion.totalMatriculados}</div></div>
    </div>
    <div style="overflow-x:auto">
      <table>
        <thead><tr>
          <th style="text-align:center">Ord.</th>
          <th>Apellidos y Nombres</th>
          <th>DNI</th>
          ${thCursos}
          <th style="text-align:center">Prom. Anual</th>
          <th style="text-align:center">Áreas Desap.</th>
          <th>Estado</th>
          <th>Observación</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
    </div>
    <div class="section-hdr" style="margin-top:16px">Resumen de la Sección</div>
    <div class="kpi-row" style="margin-top:8px">
      <div class="kpi"><div class="kpi-lbl">Promovidos</div><div class="kpi-val" style="color:#15803d">${resumen.promovidos}</div></div>
      <div class="kpi"><div class="kpi-lbl">En Recuperación</div><div class="kpi-val" style="color:#d97706">${resumen.enRecuperacion}</div></div>
      <div class="kpi"><div class="kpi-lbl">Repitentes</div><div class="kpi-val" style="color:#dc2626">${resumen.repitentes}</div></div>
      <div class="kpi"><div class="kpi-lbl">Trasladados</div><div class="kpi-val">${resumen.trasladados}</div></div>
      <div class="kpi"><div class="kpi-lbl">Retirados</div><div class="kpi-val">${resumen.retirados}</div></div>
      ${resumen.promedioSeccion !== null ? `<div class="kpi"><div class="kpi-lbl">Prom. Sección</div><div class="kpi-val">${resumen.promedioSeccion.toFixed(2)}</div></div>` : ''}
    </div>
  `;
}

export function htmlConstancia(d: ConstanciaDocData): string {
  const { institucion, estudiante, matricula, codigoVerificador } = d;
  const fechaExp = new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });

  return `
    <div style="text-align:center;margin:10px 0 20px">
      <p style="font-size:11px;color:#64748b;letter-spacing:.1em;text-transform:uppercase;margin-bottom:4px">Constancia de Matrícula</p>
      <p style="font-size:11px;color:#64748b">Código Modular: ${fmt(institucion.codigoModular)} &nbsp;|&nbsp; UGEL: ${fmt(institucion.ugel)}</p>
    </div>
    <div style="border:2px solid #1e3a8a;border-radius:10px;padding:24px;margin:0 auto;max-width:600px;text-align:center">
      <p style="font-size:12px;color:#475569;margin-bottom:16px">
        La Dirección de la Institución Educativa certifica que el(la) estudiante:
      </p>
      <p style="font-size:20px;font-weight:700;color:#1e293b;margin:0 0 6px">
        ${fmt(estudiante.apellidos)}, ${fmt(estudiante.nombres)}
      </p>
      <p style="font-size:11px;color:#64748b;margin-bottom:18px">
        DNI: ${fmt(estudiante.dni)} &nbsp;·&nbsp; Fecha de Nacimiento: ${fmtDate(estudiante.fechaNacimiento)}
      </p>
      <p style="font-size:12px;color:#475569;margin-bottom:10px">Se encuentra matriculado(a) en:</p>
      <p style="font-size:16px;font-weight:700;color:#1e3a8a;margin-bottom:4px">
        ${fmt(matricula.gradoNombre)} &mdash; Sección ${fmt(matricula.seccionNombre)}
      </p>
      <p style="font-size:12px;color:#475569;margin-bottom:4px">
        ${fmt(matricula.gradoNivel)} &nbsp;·&nbsp; Año Escolar ${matricula.anio} &nbsp;·&nbsp; Fase: ${fmtFase(matricula.fase)}
      </p>
      ${matricula.codigoMatricula ? `<p style="font-size:11px;color:#64748b;margin-bottom:4px">Código de Matrícula: <strong>${matricula.codigoMatricula}</strong></p>` : ''}
      ${matricula.fechaMatricula ? `<p style="font-size:11px;color:#64748b;margin-bottom:4px">Fecha de Matrícula: ${fmtDate(matricula.fechaMatricula)}</p>` : ''}
      <p style="font-size:11px;color:#94a3b8;margin-top:20px">Lima, ${fechaExp}</p>
      <p style="font-size:10px;color:#cbd5e1;margin-top:6px">Cód. Verificación: ${codigoVerificador}</p>
    </div>
    <div style="margin-top:20px;text-align:right;font-size:10px;color:#94a3b8">
      Director(a): ${fmt(institucion.directorNombre)}
    </div>
  `;
}

function fmtSexo(s: string | null | undefined): string {
  if (!s) return '—';
  return s === 'M' ? 'Masculino' : s === 'F' ? 'Femenino' : s;
}

function fmtTipoContrato(t: string | null | undefined): string {
  if (!t) return '—';
  const map: Record<string, string> = {
    NOMBRADO: 'Nombrado', CONTRATADO: 'Contratado', CAS: 'CAS', PRACTICANTE: 'Practicante',
  };
  return map[t] ?? t;
}

function fmtGradoAcademico(g: string | null | undefined): string {
  if (!g) return '—';
  const map: Record<string, string> = {
    BACHILLER: 'Bachiller', LICENCIADO: 'Licenciado', MAGISTER: 'Magíster', DOCTOR: 'Doctor', TECNICO: 'Técnico',
  };
  return map[g] ?? g;
}

export function htmlRod(d: RodDocData): string {
  const filas = d.asignaciones.map(a => `
    <tr>
      <td>${fmt(a.docenteApellidos)}, ${fmt(a.docenteNombres)}</td>
      <td>${fmt(a.docenteDni)}</td>
      <td>${fmtSexo(a.sexo)}</td>
      <td>${fmtGradoAcademico(a.gradoAcademico)}</td>
      <td>${fmt(a.carrera)}</td>
      <td>${fmt(a.numeroColegiatura)}</td>
      <td>${fmtTipoContrato(a.tipoContrato)}</td>
      <td style="text-align:center">${fmt(a.anioIngreso)}</td>
      <td>${fmt(a.cursoNombre)}</td>
      <td>${fmt(a.gradoNombre)}</td>
      <td>${fmt(a.gradoNivel)}</td>
      <td>${fmt(a.seccionNombre)}</td>
    </tr>`).join('');

  return `
    <div class="kpi-row">
      <div class="kpi"><div class="kpi-lbl">Año Escolar</div><div class="kpi-val">${d.anio}</div></div>
      <div class="kpi"><div class="kpi-lbl">Institución</div><div class="kpi-val" style="font-size:12px">${fmt(d.institucion.nombre)}</div></div>
      <div class="kpi"><div class="kpi-lbl">Total Asignaciones</div><div class="kpi-val">${d.asignaciones.length}</div></div>
    </div>
    <table>
      <thead><tr>
        <th>Docente</th><th>DNI</th>
        <th>Sexo</th><th>Grado Acad.</th><th>Carrera</th>
        <th>Colegiatura</th><th>Contrato</th><th>Año Ingreso</th>
        <th>Área Curricular</th><th>Grado</th><th>Nivel</th><th>Sección</th>
      </tr></thead>
      <tbody>${filas || '<tr><td colspan="12" style="text-align:center;color:#94a3b8;padding:10px">Sin asignaciones</td></tr>'}</tbody>
    </table>
  `;
}

export function htmlNomina(d: NominaDocData): string {
  const { institucion, nomina, estudiantes } = d;

  const estadoBadgeColor: Record<string, string> = {
    GENERADA:     'background:#dbeafe;color:#1d4ed8',
    APROBADA:     'background:#dcfce7;color:#15803d',
    ENVIADA_UGEL: 'background:#f3e8ff;color:#7e22ce',
    RECTIFICADA:  'background:#fef3c7;color:#d97706',
  };
  const estadoColor = estadoBadgeColor[nomina.estado] ?? 'background:#f1f5f9;color:#475569';

  const filas = estudiantes.map(e => `
    <tr>
      <td style="text-align:center">${e.orden}</td>
      <td>${fmt(e.apellidos)}, ${fmt(e.nombres)}</td>
      <td>${fmt(e.dni)}</td>
      <td>${fmtDate(e.fechaNacimiento)}</td>
      <td style="text-align:center">${fmt(e.codigoSiagie)}</td>
      <td style="text-align:center">${fmt(e.codigoMatricula)}</td>
      <td>${fmtDate(e.fechaMatricula)}</td>
      <td style="text-align:center">${fmtFase(e.fase)}</td>
    </tr>`).join('');

  return `
    <div class="kpi-row">
      <div class="kpi">
        <div class="kpi-lbl">Grado</div>
        <div class="kpi-val" style="font-size:13px">${fmt(nomina.gradoNombre)}</div>
      </div>
      <div class="kpi">
        <div class="kpi-lbl">Sección</div>
        <div class="kpi-val" style="font-size:13px">${fmt(nomina.seccionNombre)}</div>
      </div>
      <div class="kpi">
        <div class="kpi-lbl">Nivel</div>
        <div class="kpi-val" style="font-size:13px">${fmt(nomina.gradoNivel)}</div>
      </div>
      <div class="kpi">
        <div class="kpi-lbl">Año Escolar</div>
        <div class="kpi-val" style="font-size:13px">${nomina.anio}</div>
      </div>
      <div class="kpi">
        <div class="kpi-lbl">Total Matriculados</div>
        <div class="kpi-val">${nomina.totalMatriculados}</div>
      </div>
      <div class="kpi">
        <div class="kpi-lbl">Estado</div>
        <div style="margin-top:4px">
          <span class="badge" style="${estadoColor};padding:3px 10px;border-radius:99px;font-size:10px;font-weight:700">
            ${fmt(nomina.estado).replace('_', ' ')}
          </span>
        </div>
      </div>
    </div>

    <div class="section-hdr">Información de la Nómina</div>
    <table>
      <thead><tr>
        <th>Generada por</th><th>Fecha Generación</th>
        <th>Aprobada por</th><th>Fecha Aprobación</th>
        <th>Fecha Envío UGEL</th>
      </tr></thead>
      <tbody><tr>
        <td>${fmt(nomina.generadoPorNombre)}</td>
        <td>${fmtDate(nomina.fechaGeneracion)}</td>
        <td>${fmt(nomina.aprobadoPorNombre)}</td>
        <td>${fmtDate(nomina.fechaAprobacion)}</td>
        <td>${fmtDate(nomina.fechaEnvio)}</td>
      </tr></tbody>
    </table>
    ${nomina.observaciones ? `<p style="font-size:10px;color:#64748b;margin-top:6px">Observaciones: ${nomina.observaciones}</p>` : ''}

    <div class="section-hdr" style="margin-top:14px">Lista de Estudiantes Matriculados</div>
    <table>
      <thead><tr>
        <th style="text-align:center">N°</th>
        <th>Apellidos y Nombres</th>
        <th>DNI</th>
        <th>Fecha Nac.</th>
        <th style="text-align:center">Cód. SIAGIE</th>
        <th style="text-align:center">Cód. Matrícula</th>
        <th>F. Matrícula</th>
        <th style="text-align:center">Fase</th>
      </tr></thead>
      <tbody>${filas || '<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:10px">Sin estudiantes</td></tr>'}</tbody>
    </table>

    <div style="margin-top:40px;display:flex;justify-content:space-between;font-size:10px;color:#64748b">
      <div>Institución: ${fmt(institucion.nombre)} &nbsp;|&nbsp; Cód. Modular: ${fmt(institucion.codigoModular)}</div>
      <div>Director(a): ${fmt(institucion.directorNombre)}</div>
    </div>
  `;
}

export function htmlCertificado(d: CertificadoDocData): string {
  // Guard: el backend puede devolver entradas incompletas si el estudiante no tiene matrícula
  if (!d?.matriculaId || !d?.institucion || !d?.estudiante) {
    const nombre = d?.estudiante
      ? `${fmt(d.estudiante.apellidos)}, ${fmt(d.estudiante.nombres)}`
      : 'Estudiante desconocido';
    return `
      <div style="border:1px solid #fca5a5;background:#fef2f2;border-radius:8px;
                  padding:20px;text-align:center;color:#991b1b">
        <p style="font-weight:700;font-size:13px;margin-bottom:6px">${nombre}</p>
        <p style="font-size:11px">
          No se encontró matrícula activa para este estudiante en el año seleccionado.
        </p>
      </div>`;
  }

  const { institucion, estudiante, cursos, estadoPromocion, promedioAnual } = d;
  // Datos de matrícula aplanados al nivel raíz
  const matricula = {
    anio:            d.anio,
    gradoNombre:     d.gradoNombre,
    gradoNivel:      d.gradoNivel,
    seccionNombre:   d.seccionNombre,
    codigoMatricula: d.codigoMatricula,
  };

  const fechaExp = new Date().toLocaleDateString('es-PE', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  // Color del estado de promoción
  const estadoColor: Record<string, string> = {
    PROMOVIDO:       'background:#dcfce7;color:#15803d',
    REPITENTE:       'background:#fee2e2;color:#dc2626',
    EN_RECUPERACION: 'background:#fef3c7;color:#b45309',
    RETIRADO:        'background:#f1f5f9;color:#475569',
    TRASLADADO:      'background:#ede9fe;color:#6d28d9',
  };
  const estadoStyle = estadoPromocion
    ? (estadoColor[estadoPromocion] ?? 'background:#f1f5f9;color:#475569')
    : '';

  // Filas de cursos
  const filas = cursos.map(c => {
    const tieneLetra = c.notaLetra !== null;
    const valor = tieneLetra
      ? c.notaLetra!
      : c.notaFinal !== null ? c.notaFinal.toFixed(2) : '—';
    const aprobado = tieneLetra
      ? c.notaLetra === 'AD' || c.notaLetra === 'A' || c.notaLetra === 'B'
      : c.notaFinal !== null && c.notaFinal >= 11;
    const color = (c.notaFinal === null && c.notaLetra === null)
      ? '#94a3b8'
      : aprobado ? '#15803d' : '#dc2626';
    return `
      <tr>
        <td>${fmt(c.cursoNombre)}</td>
        <td style="text-align:center;font-weight:700;color:${color}">${valor}</td>
      </tr>`;
  }).join('');

  return `
    <!-- Cabecera MINEDU -->
    <div style="border:1px solid #cbd5e1;border-radius:6px;padding:10px 14px;
                margin-bottom:14px;background:#f8fafc">
      <div style="font-size:9px;color:#64748b;margin-bottom:4px">
        DRE: <strong>${fmt(institucion.dre)}</strong>
        &nbsp;|&nbsp; UGEL: <strong>${fmt(institucion.ugel)}</strong>
        &nbsp;|&nbsp; Cód. Modular: <strong>${fmt(institucion.codigoModular)}</strong>
      </div>
      <div style="font-size:14px;font-weight:700;color:#1e3a8a">${fmt(institucion.nombre)}</div>
      <div style="font-size:9px;color:#64748b;margin-top:2px">${fmt(institucion.direccion)}</div>
    </div>

    <!-- Título -->
    <div style="text-align:center;margin:16px 0 18px">
      <p style="font-size:15px;font-weight:700;color:#1e293b;text-transform:uppercase;
                letter-spacing:.08em;border-bottom:2px solid #1e3a8a;
                display:inline-block;padding-bottom:4px">
        Certificado de Estudios
      </p>
    </div>

    <!-- Cuerpo legal -->
    <p style="font-size:11px;color:#475569;text-align:justify;margin-bottom:14px;line-height:1.6">
      La Dirección de la Institución Educativa
      <strong style="color:#1e293b">${fmt(institucion.nombre)}</strong>
      certifica que el/la estudiante:
    </p>

    <!-- Datos del estudiante -->
    <div style="border:1.5px solid #1e3a8a;border-radius:8px;padding:14px 18px;
                margin-bottom:16px;background:#f8fafc">
      <p style="font-size:18px;font-weight:700;color:#1e293b;text-align:center;margin-bottom:6px">
        ${fmt(estudiante.apellidos)}, ${fmt(estudiante.nombres)}
      </p>
      <div style="font-size:10px;color:#64748b;text-align:center;margin-bottom:8px">
        DNI: <strong>${fmt(estudiante.dni)}</strong>
        ${estudiante.fechaNacimiento
          ? `&nbsp;·&nbsp; Fecha de Nac.: <strong>${fmtDate(estudiante.fechaNacimiento)}</strong>`
          : ''}
        ${estudiante.codigoSiagie
          ? `&nbsp;·&nbsp; Código SIAGIE: <strong style="font-family:monospace">${estudiante.codigoSiagie}</strong>`
          : ''}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px;margin-top:8px">
        <div style="text-align:center;background:white;border-radius:6px;padding:6px 8px;border:1px solid #e2e8f0">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700">Año Escolar</div>
          <div style="font-size:14px;font-weight:700;color:#1e293b">${matricula.anio}</div>
        </div>
        <div style="text-align:center;background:white;border-radius:6px;padding:6px 8px;border:1px solid #e2e8f0">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700">Grado</div>
          <div style="font-size:13px;font-weight:700;color:#1e293b">${fmt(matricula.gradoNombre)}</div>
        </div>
        <div style="text-align:center;background:white;border-radius:6px;padding:6px 8px;border:1px solid #e2e8f0">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700">Sección</div>
          <div style="font-size:13px;font-weight:700;color:#1e293b">${fmt(matricula.seccionNombre)}</div>
        </div>
        <div style="text-align:center;background:white;border-radius:6px;padding:6px 8px;border:1px solid #e2e8f0">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700">Nivel</div>
          <div style="font-size:13px;font-weight:700;color:#1e293b">${fmt(matricula.gradoNivel)}</div>
        </div>
      </div>
    </div>

    <!-- Tabla de calificaciones -->
    <div style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;
                letter-spacing:.05em;margin-bottom:6px">
      Calificaciones Finales
    </div>
    <table>
      <thead><tr>
        <th>Área Curricular</th>
        <th style="text-align:center;min-width:80px">Calificativo</th>
      </tr></thead>
      <tbody>
        ${filas || '<tr><td colspan="2" style="text-align:center;color:#94a3b8;padding:10px">Sin áreas registradas</td></tr>'}
      </tbody>
    </table>

    <!-- Promedio + Estado -->
    <div style="display:flex;gap:12px;margin-top:12px;flex-wrap:wrap">
      ${promedioAnual !== null ? `
        <div style="border:1px solid #e2e8f0;border-radius:6px;padding:8px 16px">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700;letter-spacing:.05em">
            Promedio Anual
          </div>
          <div style="font-size:20px;font-weight:700;margin-top:2px;
                       color:${promedioAnual >= 11 ? '#15803d' : '#dc2626'}">
            ${promedioAnual.toFixed(2)}
          </div>
        </div>` : ''}
      ${estadoPromocion ? `
        <div style="border:1px solid #e2e8f0;border-radius:6px;padding:8px 16px">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700;letter-spacing:.05em">
            Estado
          </div>
          <div style="margin-top:4px">
            <span style="padding:3px 12px;border-radius:99px;font-size:11px;font-weight:700;${estadoStyle}">
              ${fmt(estadoPromocion).replace('_', ' ')}
            </span>
          </div>
        </div>` : ''}
    </div>

    <!-- Cláusula de expedición -->
    <p style="font-size:10px;color:#64748b;margin-top:16px;text-align:justify;line-height:1.6;font-style:italic">
      Se expide el presente certificado a solicitud del interesado/a para los fines que estime conveniente.
    </p>

    <!-- Lugar y fecha -->
    <p style="font-size:10px;color:#64748b;margin-top:6px;text-align:right">
      Lima, ${fechaExp}
    </p>

    <!-- Firma del director -->
    <div style="margin-top:44px;text-align:center">
      <div style="display:inline-block;min-width:200px">
        <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:9px;color:#64748b">
          Director(a)
          <div style="font-weight:700;color:#1e293b;margin-top:2px">${fmt(institucion.directorNombre)}</div>
          <div style="font-size:8px;color:#94a3b8;margin-top:1px">
            ${fmt(institucion.nombre)}
          </div>
        </div>
      </div>
    </div>
  `;
}

export function htmlHistorial(d: HistorialDocData): string {
  const { institucion, estudiante, historial } = d;

  const filas = historial.map(h => `
    <tr>
      <td style="text-align:center">${h.anio}</td>
      <td>${fmt(h.gradoNombre)}</td>
      <td>${fmt(h.gradoNivel)}</td>
      <td>${fmt(h.seccionNombre)}</td>
      <td>${fmtDate(h.fechaMatricula)}</td>
      <td style="text-align:center;font-weight:700;color:${h.promedioAnual !== null && h.promedioAnual >= 11 ? '#15803d' : '#dc2626'}">
        ${h.promedioAnual !== null ? h.promedioAnual.toFixed(2) : '—'}
      </td>
      <td>${fmt(h.estado)}</td>
      <td style="text-align:center">${h.enCurso ? '<span class="badge" style="background:#dbeafe;color:#1d4ed8">En curso</span>' : ''}</td>
    </tr>`).join('');

  return `
    <div class="kpi-row">
      <div class="kpi">
        <div class="kpi-lbl">Estudiante</div>
        <div class="kpi-val" style="font-size:13px">${fmt(estudiante.apellidos)}, ${fmt(estudiante.nombres)}</div>
      </div>
      <div class="kpi">
        <div class="kpi-lbl">DNI</div>
        <div class="kpi-val" style="font-size:13px">${fmt(estudiante.dni)}</div>
      </div>
      ${estudiante.codigoSiagie ? `<div class="kpi"><div class="kpi-lbl">Código SIAGIE</div><div class="kpi-val" style="font-size:13px">${estudiante.codigoSiagie}</div></div>` : ''}
      <div class="kpi">
        <div class="kpi-lbl">Institución</div>
        <div class="kpi-val" style="font-size:12px">${fmt(institucion.nombre)}</div>
      </div>
    </div>
    <table>
      <thead><tr>
        <th style="text-align:center">Año</th>
        <th>Grado</th><th>Nivel</th><th>Sección</th>
        <th>Fecha Matrícula</th>
        <th style="text-align:center">Prom. Anual</th>
        <th>Estado</th><th></th>
      </tr></thead>
      <tbody>${filas || '<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:10px">Sin historial registrado</td></tr>'}</tbody>
    </table>
  `;
}

// ============================================================
// htmlBoletaSIAGIE — Informe de Progreso MINEDU (RVM N°094-2020)
// Dos columnas paralelas, 4 períodos fijos, auto-split de áreas
// ============================================================
export function htmlBoletaSIAGIE(d: BoletaSIAGIEDocData): string {

  // ── Paleta oficial MINEDU ───────────────────────────────────
  const C_AZUL   = '#003876';
  const C_AZUL2  = '#1a4f8a';
  const C_AREA   = '#dce6f1';
  const C_BORDE  = '#9ca3af';
  const B = `border:1px solid ${C_BORDE}`;

  // ── Siempre 4 columnas de período ──────────────────────────
  const PERIODOS4 = [1, 2, 3, 4].map(n => ({
    numero: n,
    nombre: d.periodos.find(p => p.numero === n)?.nombre ?? String(n),
  }));

  // ── Helper: letra con color (sin badge) ────────────────────
  const letra = (c: string | null) => {
    if (!c) return '';
    const col: Record<string, string> = {
      AD: '#155724', A: '#155724', B: '#7d4e00', C: '#721c24',
      EX: '#3730a3',  // exonerado — índigo
    };
    return `<b style="color:${col[c] ?? '#374151'}">${c}</b>`;
  };

  // ── Helper: imagen con base64, URL estática o cuadro vacío ─
  const imgBox = (b64: string | null, staticFallback: string | null = null, size = 52) => {
    // b64 puede ser data URL completo ("data:image/...;base64,...") o solo el payload base64
    const src = b64
      ? (b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`)
      : staticFallback ?? null;
    return src
      ? `<img src="${src}" style="width:${size}px;height:${size}px;object-fit:contain;display:block"/>`
      : `<div style="width:${size}px;height:${size}px;border:1px solid #d1d5db"></div>`;
  };

  // URLs de imágenes estáticas en public/images/
  const escudoUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/images/escudo_peru.jpg`
    : '/images/escudo_peru.jpg';
  const ieUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/images/ie.png`
    : '/images/ie.png';

  // ── Split fijo: 8 izquierda + 4 derecha = 12 en total ─────
  const AREAS_IZQ = 8;
  const AREAS_DER = 4;
  const splitIdx  = Math.min(AREAS_IZQ, d.areas.length > 1 ? d.areas.length - 1 : 1);
  const areasIzq  = d.areas.slice(0, splitIdx);
  const areasDer  = d.areas.slice(splitIdx);

  // ── Tabla de competencias: completa hasta `target` áreas ──
  const COMPS_VACIA = 3;   // filas de competencia por área vacía
  const tablaAreas = (areas: BoletaSIAGIEArea[], target: number) => {
    const thP = PERIODOS4.map(p =>
      `<th style="${B};text-align:center;width:16px;padding:1px;font-size:6px">${p.numero}</th>`
    ).join('');

    // ── Filas de áreas con datos ─────────────────────────────
    const dataFilas = areas.flatMap(a => {
      const n = Math.max(a.competencias.length, 1);
      const comps = a.competencias.length
        ? a.competencias
        : [{ nombre: '', calificaciones: {}, calificacionFinal: null, conclusionesPorPeriodo: {} }];
      return comps.map((comp, ci) => {
          const cals = PERIODOS4.map(p => {
            const v = comp.calificaciones[String(p.numero)] ?? null;
            return `<td style="${B};text-align:center;padding:1px">${letra(v)}</td>`;
          }).join('');
          return `<tr style="background:${ci % 2 === 0 ? '#fff' : '#f8fafc'}">
            ${ci === 0 ? `<td rowspan="${n}" style="${B};background:${C_AREA};
              padding:2px 3px;font-weight:700;font-size:6px;text-align:center;
              vertical-align:middle">${fmt(a.nombre)}</td>` : ''}
            <td style="${B};padding:1px 3px;font-size:6.5px;vertical-align:top">${fmt(comp.nombre)}</td>
            ${cals}
            <td style="${B};text-align:center;padding:1px">${letra(comp.calificacionFinal)}</td>
            ${ci === 0 ? `<td rowspan="${n}" style="${B};text-align:center;padding:1px;vertical-align:middle">
              ${letra(a.calificacionAnualArea)}
            </td>` : ''}
            ${ci === 0 ? `<td rowspan="${n}" style="${B};padding:1px 3px;font-size:6px;color:#374151;
              font-style:italic;vertical-align:top">${a.conclusionDescriptiva ?? ''}</td>` : ''}
          </tr>`;
        });
    }).join('');

    // ── Áreas vacías hasta completar `target` ───────────────
    const tdVacios = PERIODOS4.map(() => `<td style="${B};padding:2px 1px;height:13px"></td>`).join('');
    const areasVacias = Math.max(0, target - areas.length);
    const filasVacias = Array.from({ length: areasVacias }, () =>
      Array.from({ length: COMPS_VACIA }, (_, ci) => `
        <tr style="background:${ci % 2 === 0 ? '#fff' : '#f8fafc'}">
          ${ci === 0 ? `<td rowspan="${COMPS_VACIA}" style="${B};background:${C_AREA};
            padding:2px 3px;height:13px"></td>` : ''}
          <td style="${B};padding:2px 3px;height:13px"></td>
          ${tdVacios}
          <td style="${B};padding:2px 1px"></td>
          ${ci === 0 ? `<td rowspan="${COMPS_VACIA}" style="${B};padding:2px 1px"></td>` : ''}
          ${ci === 0 ? `<td rowspan="${COMPS_VACIA}" style="${B};padding:2px 3px"></td>` : ''}
        </tr>`).join('')
    ).join('');

    const filas = dataFilas + filasVacias;

    return `
      <table style="border-collapse:collapse;width:100%;font-size:7px;table-layout:fixed;margin-bottom:0">
        <colgroup>
          <col style="width:56px"/>
          <col/>
          ${PERIODOS4.map(() => '<col style="width:15px"/>').join('')}
          <col style="width:20px"/>
          <col style="width:20px"/>
          <col style="width:55px"/>
        </colgroup>
        <thead>
          <tr style="background:${C_AZUL};color:#fff;font-size:5.5px">
            <th rowspan="2" style="${B};padding:1px 2px;text-align:center;vertical-align:middle;font-size:5px;word-break:break-word">
              ÁREA<br/>CURRICULAR
            </th>
            <th rowspan="2" style="${B};padding:1px 2px;text-align:center;vertical-align:middle;font-size:5px">
              COMPETENCIAS
            </th>
            <th colspan="4" style="${B};text-align:center;padding:1px;font-size:5px">
              CALIFICATIVO<br/>POR PERIODO
            </th>
            <th rowspan="2" style="${B};text-align:center;padding:1px;vertical-align:middle;font-size:5px">
              Calif.<br/>Anual<br/>Comp.
            </th>
            <th rowspan="2" style="${B};text-align:center;padding:1px;vertical-align:middle;font-size:5px">
              Calif.<br/>Anual<br/>Área
            </th>
            <th rowspan="2" style="${B};text-align:center;padding:1px;vertical-align:middle;font-size:5px">
              Conclusión descriptiva<br/>de final del período
            </th>
          </tr>
          <tr style="background:${C_AZUL2};color:#fff">${thP}</tr>
        </thead>
        <tbody>${filas || '<tr><td colspan="9"></td></tr>'}</tbody>
      </table>`;
  };

  // ── Tabla: Conclusión descriptiva por período ───────────────
  const tblConclusiones = `
    <table style="border-collapse:collapse;width:100%;font-size:5.5px;margin-top:5px">
      <thead>
        <tr style="background:${C_AZUL};color:#fff;font-size:5px">
          <th style="${B};padding:1px 2px;text-align:center;width:40px">Período</th>
          <th style="${B};padding:1px 2px;text-align:center">Conclusión descriptiva por período</th>
        </tr>
      </thead>
      <tbody>
        ${PERIODOS4.map((p, i) => {
          const c = d.conclusionesPorPeriodo?.find(x => x.periodoNumero === p.numero);
          return `<tr style="background:${i % 2 ? '#f8fafc' : '#fff'}">
            <td style="${B};text-align:center;padding:2px;font-weight:700">${p.numero}</td>
            <td style="${B};padding:2px 4px;min-height:18px">${c?.conclusion ?? ''}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>`;

  // ── Tabla: Resumen de asistencia ────────────────────────────
  const tblAsistencia = `
    <table style="border-collapse:collapse;width:100%;font-size:5.5px;margin-top:5px">
      <thead>
        <tr style="background:${C_AZUL};color:#fff;font-size:5px">
          <th rowspan="2" style="${B};padding:1px 2px;text-align:center;width:40px;vertical-align:middle">PERÍODO</th>
          <th colspan="2" style="${B};padding:1px 2px;text-align:center">INASISTENCIAS</th>
          <th colspan="2" style="${B};padding:1px 2px;text-align:center">TARDANZAS</th>
        </tr>
        <tr style="background:${C_AZUL2};color:#fff;font-size:5px">
          <th style="${B};padding:1px 2px;text-align:center">Justificadas</th>
          <th style="${B};padding:1px 2px;text-align:center">Injustificadas</th>
          <th style="${B};padding:1px 2px;text-align:center">Justificadas</th>
          <th style="${B};padding:1px 2px;text-align:center">Injustificadas</th>
        </tr>
      </thead>
      <tbody>
        ${PERIODOS4.map((p, i) => {
          const a  = d.asistencia?.find(x => x.periodoNumero === p.numero);
          const bg = i % 2 ? '#f8fafc' : '#fff';
          const td = (v?: number) =>
            `<td style="${B};text-align:center;padding:2px;background:${bg}">${v ?? ''}</td>`;
          return `<tr>
            <td style="${B};text-align:center;padding:2px;font-weight:700;background:${bg}">${p.numero}</td>
            ${td(a?.inasistenciasJustificadas)}
            ${td(a?.inasistenciasInjustificadas)}
            ${td(a?.tardanzasJustificadas)}
            ${td(a?.tardanzasInjustificadas)}
          </tr>`;
        }).join('')}
      </tbody>
    </table>`;

  // ── Escala CNEB ─────────────────────────────────────────────
  const escala = `
    <table style="border-collapse:collapse;width:100%;font-size:6px;margin-top:5px">
      <thead>
        <tr style="background:${C_AZUL};color:#fff">
          <th colspan="2" style="${B};padding:2px 4px;text-align:center">
            ESCALA DE CALIFICACIONES DEL CNEB
          </th>
        </tr>
      </thead>
      <tbody>
        ${[
          ['AD','#c6efce','#276221','El estudiante evidencia un nivel superior a lo esperado respecto a la competencia. Esto quiere decir que demuestra aprendizajes que van más allá del nivel esperado.'],
          ['A', '#ebf5eb','#276221','El estudiante evidencia el nivel esperado respecto a la competencia, demostrando manejo satisfactorio en todas las tareas propuestas y en el tiempo programado.'],
          ['B', '#ffeb9c','#9c5700','El estudiante está próximo o cerca al nivel esperado respecto a la competencia, para lo cual requiere acompañamiento durante un tiempo razonable para lograrlo.'],
          ['C', '#ffc7ce','#9c0006','El estudiante muestra un progreso mínimo en una competencia en relación al nivel esperado. Evidencia con frecuencia dificultades en el desarrollo de las tareas, por lo que necesita mayor tiempo de acompañamiento e intervención del docente.'],
        ].map(([l, bg, col, desc]) => `
          <tr>
            <td style="${B};background:${bg};color:${col};font-weight:700;
                       text-align:center;width:18px;vertical-align:top;padding:2px">${l}</td>
            <td style="${B};padding:2px 4px;vertical-align:top">${desc}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;

  const fechaEmision = new Date().toLocaleDateString('es-PE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const version = d.institucion.version ?? '3.20.0';

  // ── HTML FINAL ──────────────────────────────────────────────
  return `
<div style="font-family:Arial,Helvetica,sans-serif;font-size:7px;color:#111827">

  <!-- ══ LAYOUT PRINCIPAL: 2 columnas ══ -->
  <table style="border-collapse:collapse;width:100%;vertical-align:top">
  <tr>

    <!-- ▌COLUMNA IZQUIERDA (~58%) ▌-->
    <td style="width:58%;vertical-align:top;padding-right:5px;border:none">

      <!-- Logo + Título + Sello -->
      <table style="border-collapse:collapse;width:100%;margin-bottom:4px">
        <tr>
          <td style="border:none;width:56px;text-align:center;vertical-align:middle">
            ${imgBox(d.institucion.escudoPeruBase64, escudoUrl)}
          </td>
          <td style="border:none;text-align:center;vertical-align:middle;padding:0 6px">
            <div style="font-size:10px;font-weight:900;color:#111827;
                        text-transform:uppercase;letter-spacing:.04em;line-height:1.3">
              INFORME DE PROGRESO DEL ESTUDIANTE – ${d.anio}
            </div>
          </td>
          <td style="border:none;width:56px;text-align:center;vertical-align:middle">
            ${imgBox(d.institucion.selloBase64 ?? null, ieUrl)}
          </td>
        </tr>
      </table>

      <!-- Datos institucionales y del estudiante -->
      <table style="border-collapse:collapse;width:100%;font-size:6.5px;margin-bottom:4px">
        <tr>
          <td style="${B};padding:2px 4px"><b>DRE:</b> ${fmt(d.institucion.dre)}</td>
          <td style="${B};padding:2px 4px"><b>UGEL:</b> ${fmt(d.institucion.ugel)}</td>
        </tr>
        <tr>
          <td style="${B};padding:2px 4px"><b>Nivel:</b> ${fmt(d.institucion.nivel)}</td>
          <td style="${B};padding:2px 4px"><b>Código Modular:</b> ${fmt(d.institucion.codigoModular)}</td>
        </tr>
        <tr>
          <td colspan="2" style="${B};padding:2px 4px">
            <b>Institución Educativa:</b> ${fmt(d.institucion.nombre)}
          </td>
        </tr>
        <tr>
          <td style="${B};padding:2px 4px"><b>Grado:</b> ${fmt(d.grado)}</td>
          <td style="${B};padding:2px 4px"><b>Sección:</b> ${fmt(d.seccion)}</td>
        </tr>
        <tr>
          <td colspan="2" style="${B};padding:2px 4px">
            <b>Apellido y nombres del estudiante:</b>
            ${fmt(d.estudiante.apellidos)}, ${fmt(d.estudiante.nombres)}
          </td>
        </tr>
        <tr>
          <td style="${B};padding:2px 4px">
            <b>Código del estudiante:</b> ${fmt(d.estudiante.codigoEstudiante)}
          </td>
          <td style="${B};padding:2px 4px"><b>DNI:</b> ${fmt(d.estudiante.dni)}</td>
        </tr>
      </table>

      <!-- Tabla de competencias — columna izquierda (8 áreas) -->
      ${tablaAreas(areasIzq, AREAS_IZQ)}

    </td><!-- /COLUMNA IZQUIERDA -->

    <!-- ▌COLUMNA DERECHA (~42%) ▌-->
    <td style="width:42%;vertical-align:top;border:none">

      <!-- Continuación de la tabla de competencias — columna derecha (4 áreas) -->
      ${tablaAreas(areasDer, AREAS_DER)}

      <!-- Conclusión descriptiva por período -->
      ${tblConclusiones}

      <!-- Resumen de asistencia -->
      ${tblAsistencia}

      <!-- Escala CNEB -->
      ${escala}

    </td><!-- /COLUMNA DERECHA -->

  </tr>
  </table><!-- /LAYOUT PRINCIPAL -->

  <!-- ══ PIE DE PÁGINA — ancho completo ══ -->
  <table style="border-collapse:collapse;width:100%;margin-top:28px">
    <tr>
      <td style="border:none;text-align:center;padding:0 12px">
        <div style="border-top:1px solid #6b7280;padding-top:4px;font-size:7px;
                    color:#374151;margin-top:30px">
          Firma y sello del Docente o Tutor(a)
          ${d.institucion.tutorNombre
            ? `<div style="font-weight:700;color:#111827;margin-top:1px">
                 ${fmt(d.institucion.tutorNombre)}
               </div>`
            : ''}
        </div>
      </td>
      <td style="border:none;text-align:center;padding:0 12px">
        <div style="border-top:1px solid #6b7280;padding-top:4px;font-size:7px;
                    color:#374151;margin-top:30px">
          Firma y sello del director(a)
          ${d.institucion.directorNombre
            ? `<div style="font-weight:700;color:#111827;margin-top:1px">
                 ${fmt(d.institucion.directorNombre)}
               </div>`
            : ''}
        </div>
      </td>
    </tr>
  </table>

  <div style="margin-top:5px;text-align:right;font-size:6px;color:#6b7280">
    Emitido: ${fechaEmision} &nbsp;|&nbsp; SAE versión ${version}
  </div>

</div>`;
}

